<template>
  <!-- Single root: attrs like class/style will land here -->
  <div class="game-timer" v-bind="$attrs" :class="{ 'fade-out': shouldFadeOut }">
    <div
      id="timer"
      :class="{ 
        'pulse-red': isRedPhase
      }"
      role="timer"
      aria-live="polite"
      :aria-label="gameStore.timeRemaining > 0 ? `${gameStore.timeRemaining}s remaining` : 'Game over'"
    >
      <!-- Circular progress background -->
      <div class="timer-circle">
        <svg class="timer-svg" viewBox="0 0 100 100" aria-hidden="true" focusable="false">
          <circle 
            cx="50" 
            cy="50" 
            r="45" 
            fill="none" 
            stroke="#fff" 
            stroke-width="6"
          />
          <circle 
            cx="50" 
            cy="50" 
            r="45" 
            fill="none" 
            :stroke="currentColor"
            stroke-width="6"
            stroke-linecap="round"
            :stroke-dasharray="circumference"
            :stroke-dashoffset="dashOffset"
            transform="rotate(-90 50 50)"
            class="progress-circle"
          />
        </svg>
        <div class="timer-content">
          <div v-if="gameStore.timeRemaining > 0">
            <span>{{ gameStore.timeRemaining }}s</span>
          </div>
          <div v-else>
            <h4>Game Over!</h4>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Round Counter -->
    <div 
      id="round-counter" 
      v-if="gameStore.gameStarted && gameStore.roundNumber > 0"
    >
      Round {{ gameStore.roundNumber }}
    </div>
  </div>
</template>

<script setup>
import { computed, defineOptions, ref, watch } from 'vue';
import { useGameStore } from '@/stores/gameStore';

// Prevent Vue auto-applying attrs to an inner node;
// we'll forward them to the wrapper with v-bind="$attrs"
defineOptions({ inheritAttrs: false });

const props = defineProps({
  departmentPrimaryColor: {
    type: String,
    default: '#07418C'
  }
});

const gameStore = useGameStore();

// SVG circle properties
const radius = 45;
const circumference = computed(() => 2 * Math.PI * radius);

// Compute the current total time dynamically based on timer phase
const currentTotalTime = computed(() => {
  // Round 1 (first round): use initial timerLength (120s)
  // Round 2+ (subsequent rounds): use secondaryTimerLength (60s)
  return gameStore.roundNumber === 1 ? gameStore.timerLength : gameStore.secondaryTimerLength;
});

// Compute the current color for the progress circle
const currentColor = computed(() => {
  const totalTime = currentTotalTime.value || 1;
  const remainingTime = Math.max(0, gameStore.timeRemaining);
  const progress = remainingTime / totalTime;
  
  if (remainingTime <= 0) return '#F60F01';
  if (progress > 0.66) return props.departmentPrimaryColor;
  if (progress > 0.33) return 'rgb(255, 115, 0)';
  return '#F60F01';
});

// Compute the dash offset for smooth circular progress
const dashOffset = computed(() => {
  const totalTime = currentTotalTime.value || 1;
  const remainingTime = Math.max(0, gameStore.timeRemaining);
  const progress = Math.min(1, Math.max(0, remainingTime / totalTime));
  return circumference.value * (1 - progress);
});

// Detect when timer is in red phase for pulsing animation
const isRedPhase = computed(() => {
  const totalTime = currentTotalTime.value || 1;
  const remainingTime = Math.max(0, gameStore.timeRemaining);
  const progress = remainingTime / totalTime;
  return progress <= 0.33 && remainingTime > 0;
});

// Fade out logic for game over timer
const shouldFadeOut = ref(false);

// Watch for when game finishes and start fade-out after 3 seconds
watch(() => gameStore.finishGame, (newFinishGame) => {
  if (newFinishGame) {
    // Start fade-out after 3 seconds
    setTimeout(() => {
      shouldFadeOut.value = true;
    }, 1500);
  }
  // Don't reset fade-out when game restarts - let it stay hidden
});

// Reset fade-out when a new game actually starts
watch(() => gameStore.gameStarted, (newGameStarted) => {
  if (newGameStarted) {
    shouldFadeOut.value = false;
  }
});

</script>

<style src="../styles/_timer.scss" lang="scss"></style>
